<template>
	<div class="secretsManagerRoot">
		<div class="pageHeading">Secrets Manager</div>
		<div class="tabRow" role="tablist">
			<router-link :to="{ name: 'clientSecrets' }" class="tabLink" :class="{ activeTab: !isAgentsView }" role="tab" :aria-selected="String(!isAgentsView)">Secrets</router-link>
			<router-link :to="{ name: 'clientSecretsAgents' }" class="tabLink" :class="{ activeTab: isAgentsView }" role="tab" :aria-selected="String(isAgentsView)">Agent Authorization</router-link>
		</div>

		<div v-if="error" class="errorBox">{{error}}</div>

		<!-- Agent Authorization view -->
		<template v-if="isAgentsView">
			<div class="card">
				<div v-if="loadingAgents" class="loadingRow"><ScaleLoader /> Loading agents…</div>
				<template v-else>
					<p class="hintText">No agent is authorized for secrets synchronization by default. Slide a control below to authorize or de-authorize an agent. De-authorizing an agent stops synchronization but does not clear the secrets the agent already has.</p>
					<p v-if="!agents.length" class="emptyMessage">No agents are configured.</p>
					<div v-for="agent in agents" :key="agent.AgentId" class="agentAuthRow">
						<div class="agentAuthInfo">
							<span class="statusDot" :class="agent.IsConnected ? 'dotGreen' : 'dotGray'" :title="agent.IsConnected ? 'connected' : 'disconnected'"></span>
							<span class="agentAuthName">{{agent.AgentName}}</span>
							<span class="agentAuthState" :class="{ stateAuthorized: agent.IsAuthorized }">{{agent.IsAuthorized ? 'authorized' : 'not authorized'}}</span>
							<span v-if="!agent.IsConnected" class="mutedText">(disconnected)</span>
						</div>
						<slide-button :key="'slideBtn_agent_' + agent.AgentId + '_' + agentSlideBtnCounters[agent.AgentId]"
									  :auto-width="false"
									  :circle="true"
									  :noanimate="true"
									  :width="300"
									  :height="60"
									  :text="agent.IsAuthorized ? 'slide to de-authorize' : 'slide to authorize'"
									  :success-text="agent.IsAuthorized ? 'de-authorizing' : 'authorizing'"
									  @completed="onAgentAuthCommanded(agent)"
									  :requireReleaseToComplete="true" />
					</div>
				</template>
			</div>
		</template>

		<!-- Secrets list view -->
		<template v-else>
			<div class="card toolbarCard">
				<input type="text" class="filterInput" placeholder="Filter secrets…" v-model="filterText" spellcheck="false" aria-label="Filter secrets" />
				<button type="button" class="gradientButton green" @click="openNewSecretDialog">Add Secret</button>
				<button type="button" class="gradientButton lightBlue" @click="maskingEnabled = !maskingEnabled">{{maskingEnabled ? 'Show Values' : 'Mask Values'}}</button>
			</div>
			<div class="card">
				<div v-if="loadingSecrets" class="loadingRow"><ScaleLoader /> Loading secrets…</div>
				<template v-else>
					<div v-if="secrets.length" class="listMeta">{{filterText ? filteredSecrets.length + ' of ' + secrets.length : String(secrets.length)}} secret{{secrets.length === 1 ? '' : 's'}}</div>
					<div v-if="filteredSecrets.length" class="listHeader" aria-hidden="true">
						<div>Name / Value</div>
						<div>Sync</div>
						<div>Last edit</div>
					</div>
					<div v-for="secret in filteredSecrets" :key="secret.Key" class="secretRow">
						<div class="secretMain">
							<button type="button" class="secretNameBtn" @click="openSecretDialog(secret)" :title="'Edit secret \'' + secret.Key + '\''">{{secret.Key}}</button>
							<div class="secretValueRow">
								<span class="secretValue" :class="{ amasked: maskingEnabled && !isBlankValue(secret.Value), blankValue: isBlankValue(secret.Value) }">{{displayValue(secret.Value)}}</span>
								<button v-if="!isBlankValue(secret.Value)" type="button" class="copyBtn" @click="copyValue(secret)" :title="'Copy the value of \'' + secret.Key + '\' to the clipboard'">Copy</button>
							</div>
						</div>
						<div><span class="syncBadge" :class="syncBadgeClass(secret)" :title="secret.SyncTooltip">{{secret.SyncSummary}}</span></div>
						<div class="lastEditCell">{{formatLastEdit(secret)}}</div>
					</div>
					<p v-if="!filteredSecrets.length" class="emptyMessage">{{secrets.length ? 'No secrets match the filter.' : 'No secrets exist yet. Add one, or authorize an agent to discover the secrets it already has.'}}</p>
				</template>
			</div>
			<div class="card">
				<div class="hintText downloadHint">Download a copy of <code class="inline">secrets.json</code> suitable for placing on a new server that is not connected via TdsAppReleaseAgent.</div>
				<slide-button v-if="!downloadRevealed"
							  :key="'slideBtn_download_' + downloadSlideBtnCounter"
							  :auto-width="false"
							  :circle="true"
							  :noanimate="true"
							  :width="300"
							  :height="60"
							  text="slide to download secrets.json"
							  success-text="revealed"
							  @completed="onDownloadRevealed"
							  :requireReleaseToComplete="true" />
				<a v-else class="gradientButton green downloadLink" href="#" @click.prevent="downloadSecretsJson">Download secrets.json</a>
			</div>
		</template>

		<!-- Edit/Add dialog -->
		<div v-if="editDialogVisible" class="secretDialogOverlay" @click.self="tryCloseDialog">
			<div class="secretDialog" role="dialog" aria-modal="true" :aria-label="editOriginalKey ? 'Edit Secret' : 'Add Secret'">
				<div class="secretDialogTitle">{{editOriginalKey ? 'Edit Secret' : 'Add Secret'}}</div>
				<label class="fieldLabel" for="secretNameInput">Name</label>
				<input id="secretNameInput" ref="keyInput" type="text" class="fieldInput" v-model="editKey" spellcheck="false" autocomplete="off" placeholder="e.g. MyApiKey" />
				<label class="fieldLabel" for="secretValueInput">Value</label>
				<textarea id="secretValueInput" class="fieldInput valueInput" v-model="editValue" rows="5" spellcheck="false" autocomplete="off"></textarea>
				<div class="generateRow">
					<button type="button" class="copyBtn generateBtn" @click="generateRandomValue" :disabled="saving" aria-describedby="secretGenerateHint">Generate random value</button>
					<label class="generateSizeLabel" for="secretGenerateBytes">Size</label>
					<select id="secretGenerateBytes" class="generateSizeSelect" v-model.number="generateByteCount">
						<option v-for="n in generateByteOptions" :key="n" :value="n">{{n}} bytes ({{base64Length(n)}} characters)</option>
					</select>
				</div>
				<div id="secretGenerateHint" class="hintText generateHint">Fills the value with base64 of cryptographically random bytes, generated in this browser.  Nothing is saved until you click Save.</div>
				<div v-if="editSecret" class="metadataSection">
					<div class="metaRow"><span class="metaLabel">Created</span>{{formatUtc(editSecret.CreatedUtc)}} by {{editSecret.CreatedBy || 'unknown'}}</div>
					<div class="metaRow"><span class="metaLabel">Modified</span>{{formatUtc(editSecret.ModifiedUtc)}} by {{editSecret.ModifiedBy || 'unknown'}}</div>
					<div class="metaRow"><span class="metaLabel">Sync</span><span class="syncBadge" :class="syncBadgeClass(editSecret)" :title="editSecret.SyncTooltip">{{editSecret.SyncSummary}}</span></div>
					<div v-if="editAgentSync && editAgentSync.length" class="agentSyncList">
						<div v-for="a in editAgentSync" :key="a.AgentId" class="agentSyncItem">
							<span class="statusDot" :class="a.IsSynced ? 'dotGreen' : 'dotRed'"></span>
							<span>{{a.AgentName}}: <span :class="a.IsSynced ? 'syncedText' : 'notSyncedText'">{{a.IsSynced ? 'synced' : 'not synced'}}</span><span v-if="!a.IsConnected" class="mutedText"> (disconnected)</span></span>
						</div>
					</div>
				</div>
				<div class="secretDialogButtons">
					<button type="button" class="gradientButton green" @click="saveSecret" :disabled="saving">Save</button>
					<button type="button" class="gradientButton lightBlue" @click="tryCloseDialog">Cancel</button>
					<button v-if="editOriginalKey" type="button" class="gradientButton tdsHealthBrown deleteBtn" @click="deleteSecret" :disabled="saving">Delete</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	import SlideButton from 'appRoot/vues/common/controls/SlideButton.vue';
	import { ModalConfirmDialog } from 'appRoot/scripts/ModalDialog';
	import { CopyToClipboard } from 'appRoot/scripts/Util';

	/**
	 * Returns base64 (standard alphabet, padded) of the given number of cryptographically random bytes from
	 * crypto.getRandomValues.  The value stays in the browser until the user saves it; never log it.
	 */
	function RandomBase64(byteCount)
	{
		let bytes = new Uint8Array(byteCount);
		window.crypto.getRandomValues(bytes);
		let binary = '';
		for (let i = 0; i < bytes.length; i++)
			binary += String.fromCharCode(bytes[i]);
		return window.btoa(binary);
	}
	import
		{
			GetSecretsList, GetSecretDetail, SaveSecret, DeleteSecret,
			GetAgentAuthorization, SetAgentAuthorization, DownloadSecretsJson
		} from 'appRoot/api/SecretsManagerData';

	export default {
		components: { SlideButton },
		data()
		{
			return {
				error: null,
				loadingSecrets: true,
				loadingAgents: false,
				secrets: [],
				agents: [],
				filterText: '',
				maskingEnabled: true,
				downloadRevealed: false,
				downloadSlideBtnCounter: 0,
				agentSlideBtnCounters: {},
				editDialogVisible: false,
				editOriginalKey: null,
				editOriginalValue: '',
				editKey: '',
				editValue: '',
				editSecret: null,
				editAgentSync: null,
				saving: false,
				confirmingClose: false,
				confirmingGenerate: false,
				generateByteCount: 32,
				generateByteOptions: [32, 48, 64],
				refreshTimer: null,
				silentRefreshBusy: false,
			};
		},
		computed:
		{
			isAgentsView()
			{
				return this.$route.name === 'clientSecretsAgents';
			},
			editDirty()
			{
				if (!this.editDialogVisible)
					return false;
				return this.editKey !== (this.editOriginalKey || '') || this.editValue !== this.editOriginalValue;
			},
			filteredSecrets()
			{
				let filter = (this.filterText || '').trim().toLowerCase();
				if (!filter)
					return this.secrets;
				return this.secrets.filter(s =>
				{
					let key = (s.Key || '').toLowerCase();
					let value = (s.Value || '').toLowerCase();
					return key.indexOf(filter) >= 0 || value.indexOf(filter) >= 0;
				});
			}
		},
		watch:
		{
			isAgentsView(val)
			{
				if (val)
					this.loadAgents();
				else
					this.loadSecrets();
			},
			editDialogVisible(visible)
			{
				if (visible)
				{
					document.addEventListener('keydown', this.onDocumentKeyDown);
					this.$nextTick(() =>
					{
						if (this.$refs.keyInput)
							this.$refs.keyInput.focus();
					});
				}
				else
					document.removeEventListener('keydown', this.onDocumentKeyDown);
			}
		},
		created()
		{
			if (this.isAgentsView)
				this.loadAgents();
			else
				this.loadSecrets();
			// Sync with agents runs in the background on the server, so refresh silently to keep sync status current.
			this.refreshTimer = setInterval(this.silentRefresh, 8000);
		},
		beforeUnmount()
		{
			clearInterval(this.refreshTimer);
			document.removeEventListener('keydown', this.onDocumentKeyDown);
		},
		methods:
		{
			loadSecrets()
			{
				this.loadingSecrets = true;
				this.error = null;
				GetSecretsList()
					.then(result =>
					{
						this.loadingSecrets = false;
						if (result.error)
						{
							this.error = result.error;
							return;
						}
						this.secrets = result.secrets || [];
					})
					.catch(ex =>
					{
						this.loadingSecrets = false;
						this.error = ex.message || String(ex);
					});
			},
			loadAgents()
			{
				this.loadingAgents = true;
				this.error = null;
				GetAgentAuthorization()
					.then(result =>
					{
						this.loadingAgents = false;
						if (result.error)
						{
							this.error = result.error;
							return;
						}
						this.applyAgents(result.agents || []);
					})
					.catch(ex =>
					{
						this.loadingAgents = false;
						this.error = ex.message || String(ex);
					});
			},
			applyAgents(agents)
			{
				this.agents = agents;
				for (let i = 0; i < this.agents.length; i++)
				{
					let id = this.agents[i].AgentId;
					if (typeof this.agentSlideBtnCounters[id] === 'undefined')
						this.agentSlideBtnCounters[id] = 0;
				}
			},
			silentRefresh()
			{
				if (document.hidden || this.silentRefreshBusy)
					return;
				if (this.isAgentsView)
				{
					if (this.loadingAgents)
						return;
					this.silentRefreshBusy = true;
					GetAgentAuthorization()
						.then(result =>
						{
							// Only apply the new data if something changed, so re-rendering does not disturb an in-progress slide gesture.
							if (!result.error && result.agents && JSON.stringify(result.agents) !== JSON.stringify(this.agents))
								this.applyAgents(result.agents);
						})
						.catch(() => { })
						.finally(() => { this.silentRefreshBusy = false; });
				}
				else
				{
					if (this.loadingSecrets)
						return;
					this.silentRefreshBusy = true;
					GetSecretsList()
						.then(result =>
						{
							// Only apply the new data if something changed, so re-rendering does not clear a text selection the user made to view a masked value.
							if (!result.error && result.secrets && JSON.stringify(result.secrets) !== JSON.stringify(this.secrets))
								this.secrets = result.secrets;
						})
						.catch(() => { })
						.finally(() => { this.silentRefreshBusy = false; });
				}
			},
			displayValue(value)
			{
				if (this.isBlankValue(value))
					return '<blank>';
				else if (this.maskingEnabled)
					return '••••••••••••••••';
				return value;
			},
			isBlankValue(value)
			{
				return value === null || value === undefined || value === '';
			},
			secretKeyExists(key, ignoreKey)
			{
				let normalizedKey = (key || '').toLowerCase();
				let normalizedIgnore = ignoreKey ? ignoreKey.toLowerCase() : null;
				return this.secrets.some(s =>
				{
					let existingKey = (s.Key || '').toLowerCase();
					if (normalizedIgnore && existingKey === normalizedIgnore)
						return false;
					return existingKey === normalizedKey;
				});
			},
			syncBadgeClass(secret)
			{
				if (!secret || typeof secret.TotalAgentCount === 'undefined')
					return 'syncNoAgents';
				if (secret.TotalAgentCount === 0)
					return 'syncNoAgents';
				if (secret.SyncedAgentCount === secret.TotalAgentCount)
					return 'syncAll';
				if (secret.SyncedAgentCount > 0)
					return 'syncPartial';
				return 'syncNone';
			},
			copyValue(secret)
			{
				CopyToClipboard(secret.Value);
				toaster.Success('Copied the value of "' + secret.Key + '" to the clipboard.');
			},
			formatLastEdit(secret)
			{
				return this.formatUtc(secret.ModifiedUtc) + ',\n by ' + (secret.ModifiedBy || 'unknown');
			},
			formatUtc(utcStr)
			{
				if (!utcStr)
					return '';
				let d = new Date(utcStr);
				if (isNaN(d.getTime()))
					return utcStr;
				return d.toLocaleString();
			},
			onDocumentKeyDown(e)
			{
				if (e.key === 'Escape' && this.editDialogVisible && !this.confirmingGenerate)
					this.tryCloseDialog();
			},
			base64Length(byteCount)
			{
				return 4 * Math.ceil(byteCount / 3);
			},
			generateRandomValue()
			{
				if (this.confirmingGenerate)
					return;
				if (!window.crypto || typeof window.crypto.getRandomValues !== 'function')
				{
					toaster.Error('This browser does not provide a cryptographically strong random number generator.');
					return;
				}
				let byteCount = this.generateByteCount;
				let apply = () =>
				{
					this.editValue = RandomBase64(byteCount);
				};
				if (this.isBlankValue(this.editValue))
				{
					apply();
					return;
				}
				this.confirmingGenerate = true;
				ModalConfirmDialog('Replace the current value with a new random value?\n\nNothing is saved until you click Save.', 'Generate Random Value', 'Replace', 'Cancel')
					.then(confirmed =>
					{
						this.confirmingGenerate = false;
						if (confirmed)
							apply();
					});
			},
			openSecretDialog(secret)
			{
				this.editOriginalKey = secret.Key;
				this.editOriginalValue = secret.Value == null ? '' : secret.Value;
				this.editKey = secret.Key;
				this.editValue = this.editOriginalValue;
				this.editSecret = secret;
				this.editAgentSync = null;
				this.editDialogVisible = true;
				GetSecretDetail(secret.Key)
					.then(result =>
					{
						if (!result.error && result.secret)
						{
							this.editSecret = result.secret;
							this.editAgentSync = result.agentSync;
						}
					})
					.catch(ex =>
					{
						toaster.Error(ex.message || String(ex));
					});
			},
			tryCloseDialog()
			{
				if (this.confirmingClose)
					return;
				if (!this.editDirty)
				{
					this.editDialogVisible = false;
					return;
				}
				this.confirmingClose = true;
				ModalConfirmDialog('You have unsaved changes.', 'Unsaved Changes', 'Close', 'Stay Open')
					.then(result =>
					{
						this.confirmingClose = false;
						if (result)
							this.editDialogVisible = false;
					});
			},
			openNewSecretDialog()
			{
				this.editOriginalKey = null;
				this.editOriginalValue = '';
				this.editKey = '';
				this.editValue = '';
				this.editSecret = null;
				this.editAgentSync = [];
				this.editDialogVisible = true;
			},
			saveSecret()
			{
				let key = this.editKey ? this.editKey.trim() : '';
				if (!key)
				{
					toaster.Error('Secret name is required.');
					return;
				}
				if (!/^[A-Za-z_][A-Za-z_0-9.]*$/.test(key))
				{
					toaster.Error('Secret name must be a valid variable identifier: only letters, digits, underscore, and period, not beginning with a digit or period.');
					return;
				}
				if (this.secretKeyExists(key, this.editOriginalKey))
				{
					toaster.Error('A secret with that name already exists.');
					return;
				}
				this.saving = true;
				SaveSecret(this.editOriginalKey, key, this.editValue)
					.then(result =>
					{
						this.saving = false;
						if (result.error)
						{
							toaster.Error(result.error);
							return;
						}
						this.editDialogVisible = false;
						this.loadSecrets();
					})
					.catch(ex =>
					{
						this.saving = false;
						toaster.Error(ex.message || String(ex));
					});
			},
			deleteSecret()
			{
				if (!this.editOriginalKey)
					return;
				ModalConfirmDialog('Delete secret "' + this.editOriginalKey + '"?\n\nThe secret will also be deleted from all authorized agents.', 'Delete Secret', 'Delete', 'Cancel')
					.then(confirmed =>
					{
						if (!confirmed)
							return;
						this.saving = true;
						DeleteSecret(this.editOriginalKey)
							.then(result =>
							{
								this.saving = false;
								if (result.error)
								{
									toaster.Error(result.error);
									return;
								}
								this.editDialogVisible = false;
								this.loadSecrets();
							})
							.catch(ex =>
							{
								this.saving = false;
								toaster.Error(ex.message || String(ex));
							});
					});
			},
			onDownloadRevealed()
			{
				this.downloadRevealed = true;
			},
			downloadSecretsJson()
			{
				DownloadSecretsJson()
					.then(result =>
					{
						if (result.error)
						{
							toaster.Error(result.error);
							return;
						}
						let blob = new Blob([result.secretsJson], { type: 'application/json' });
						let url = URL.createObjectURL(blob);
						let a = document.createElement('a');
						a.href = url;
						a.download = 'secrets.json';
						a.click();
						URL.revokeObjectURL(url);
					})
					.catch(ex =>
					{
						toaster.Error(ex.message || String(ex));
					});
			},
			onAgentAuthCommanded(agent)
			{
				let newAuthorized = !agent.IsAuthorized;
				SetAgentAuthorization(agent.AgentId, newAuthorized)
					.then(result =>
					{
						if (result.error)
							toaster.Error(result.error);
						else
							agent.IsAuthorized = newAuthorized;
					})
					.catch(ex =>
					{
						toaster.Error(ex.message || String(ex));
					})
					.finally(() =>
					{
						this.agentSlideBtnCounters[agent.AgentId]++;
						this.silentRefresh();
					});
			}
		}
	}
</script>

<style scoped>
	/* Match the page background and padding used by other client pages (e.g. the Release tab's .pageRoot). */
	.secretsManagerRoot
	{
		padding: 8px 8px 1em 8px;
		background-color: #EFEDE6;
		overflow-y: auto;
	}

		/* Keep the content column a comfortable reading width on large monitors.  The dialog overlay is position:fixed and must not be constrained. */
		.secretsManagerRoot > :not(.secretDialogOverlay)
		{
			max-width: 950px;
		}

	.pageHeading
	{
		font-size: 32px;
		margin-bottom: 0.33em;
	}

	/* Tabs */
	.tabRow
	{
		display: flex;
		flex-wrap: wrap;
		border-bottom: 2px solid #CCCCCC;
		margin-bottom: 1.25em;
	}

	.tabLink
	{
		padding: 8px 18px;
		font-size: 17px;
		color: #333333;
		text-decoration: none;
		border-bottom: 2px solid transparent;
		margin-bottom: -2px;
	}

		.tabLink:hover
		{
			background-color: #EEEEEE;
		}

		.tabLink.activeTab
		{
			font-weight: bold;
			color: var(--primary-color-text);
			border-bottom-color: var(--primary-color);
		}

	/* Cards (visually consistent with .outerContainer on the Release tab) */
	.card
	{
		border: 1px solid #DDDDDD;
		padding: 12px;
		background-color: #FFFFFF;
		margin-bottom: 1.5em;
		box-shadow: 2px 2px 2px rgba(0,0,0,0.4);
	}

	.errorBox
	{
		background-color: #FFE0E0;
		border: 1px solid #C00000;
		color: #660000;
		padding: 8px 12px;
		margin-bottom: 1em;
		white-space: pre-wrap;
		word-break: break-word;
	}

	.loadingRow
	{
		display: flex;
		align-items: center;
		gap: 10px;
		padding: 8px;
	}

	.hintText
	{
		color: #555555;
		font-size: 0.92em;
		margin: 0 0 10px 0;
	}

	.mutedText
	{
		color: #888888;
	}

	.emptyMessage
	{
		color: #666666;
		font-style: italic;
		margin: 8px 0;
	}

	/* Toolbar */
	.toolbarCard
	{
		display: flex;
		flex-wrap: wrap;
		gap: 8px;
		align-items: center;
	}

	.filterInput
	{
		flex: 1 1 200px;
		min-width: 140px;
		max-width: 400px;
		padding: 7px 10px;
		border: 1px solid #AAAAAA;
		border-radius: 4px;
		font-size: 15px;
		box-sizing: border-box;
	}

	button.gradientButton:disabled
	{
		opacity: 0.55;
		pointer-events: none;
	}

	/* Secrets list */
	.listMeta
	{
		color: #666666;
		font-size: 0.85em;
		margin-bottom: 6px;
	}

	.listHeader,
	.secretRow
	{
		display: grid;
		grid-template-columns: minmax(0, 1fr) 90px 210px;
		gap: 4px 12px;
		align-items: start;
	}

	.listHeader
	{
		font-weight: bold;
		color: #555555;
		font-size: 0.8em;
		text-transform: uppercase;
		letter-spacing: 0.05em;
		padding: 4px 6px;
		border-bottom: 2px solid #DDDDDD;
	}

	.secretRow
	{
		padding: 10px 6px;
		border-bottom: 1px solid #E4E4E4;
	}

		.secretRow:hover
		{
			background-color: #F5FAFF;
		}

	.secretMain
	{
		min-width: 0;
	}

	.secretNameBtn
	{
		background: none;
		border: none;
		padding: 0;
		color: #0066CC;
		font-weight: bold;
		font-size: 1.05em;
		cursor: pointer;
		text-align: left;
		word-break: break-all;
	}

		.secretNameBtn:hover,
		.secretNameBtn:focus
		{
			text-decoration: underline;
		}

	.secretValueRow
	{
		margin-top: 4px;
		display: flex;
		align-items: flex-start;
		gap: 8px;
		min-width: 0;
	}

	.secretValue
	{
		min-width: 0;
		font-family: Consolas, monospace;
		font-size: 0.9em;
		background-color: #F4F4F4;
		border-radius: 3px;
		padding: 2px 6px;
		word-break: break-all;
		white-space: pre-wrap;
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
	}

		/* Masking is implemented by making the text and background color identical, so the user may select the text to make it visible. */
		.secretValue.masked
		{
			background: rgba(133, 133, 133, 0.3);
			background: linear-gradient(90deg, rgb(153 153 153 / 30%) 0%, rgba(92, 92, 92, 0.3) 25%, rgb(147 147 147 / 30%) 50%, rgba(118, 118, 118, 0.3) 75%, rgb(173 173 173 / 30%) 100%);
			color: transparent;
			user-select: text;
			-webkit-line-clamp: 1;
			width: 150px;
		}

		.secretValue.blankValue
		{
			font-family: inherit;
			font-style: italic;
			opacity: 0.6;
			background: none;
		}

	.copyBtn
	{
		flex: 0 0 auto;
		font-size: 0.8em;
		padding: 2px 8px;
		border: 1px solid #999999;
		border-radius: 3px;
		background-color: #FAFAFA;
		cursor: pointer;
	}

		.copyBtn:hover
		{
			background-color: #FFFFFF;
			border-color: var(--primary-color);
		}

	.syncBadge
	{
		display: inline-block;
		padding: 2px 10px;
		border-radius: 10px;
		font-weight: bold;
		font-size: 0.9em;
		white-space: nowrap;
		cursor: help;
	}

	.syncAll
	{
		background-color: #D4F5D4;
		color: #0A5A0A;
		border: 1px solid #77CC77;
	}

	.syncPartial
	{
		background-color: #FFEFC9;
		color: #7A5200;
		border: 1px solid #E0B93F;
	}

	.syncNone
	{
		background-color: #FFDBDB;
		color: #8B0000;
		border: 1px solid #E08585;
	}

	.syncNoAgents
	{
		background-color: #EEEEEE;
		color: #666666;
		border: 1px solid #CCCCCC;
	}

	.lastEditCell
	{
		color: #555555;
		font-size: 0.9em;
		white-space: pre-line;
	}

	.downloadHint
	{
		margin-bottom: 10px;
	}

	a.downloadLink
	{
		max-width: none;
	}

	/* Agent Authorization view */
	.agentAuthRow
	{
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		justify-content: space-between;
		gap: 10px 16px;
		padding: 12px 6px;
		border-bottom: 1px solid #E4E4E4;
	}

	.agentAuthInfo
	{
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 8px;
		min-width: 0;
	}

	.agentAuthName
	{
		font-weight: bold;
		font-size: 1.05em;
		word-break: break-all;
	}

	.agentAuthState
	{
		font-size: 0.85em;
		padding: 1px 8px;
		border-radius: 9px;
		background-color: #EEEEEE;
		color: #666666;
		white-space: nowrap;
	}

		.agentAuthState.stateAuthorized
		{
			background-color: #D4F5D4;
			color: #0A5A0A;
		}

	.statusDot
	{
		flex: 0 0 auto;
		display: inline-block;
		width: 10px;
		height: 10px;
		border-radius: 50%;
	}

	.dotGreen
	{
		background-color: #2E9E2E;
	}

	.dotGray
	{
		background-color: #AAAAAA;
	}

	.dotRed
	{
		background-color: #CC3333;
	}

	/* Edit/Add dialog */
	.secretDialogOverlay
	{
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background-color: rgba(0,0,0,0.45);
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 1000;
		padding: 12px;
		box-sizing: border-box;
	}

	.secretDialog
	{
		background-color: #FFFFFF;
		padding: 18px 20px;
		width: 100%;
		max-width: 560px;
		max-height: calc(100vh - 24px);
		overflow-y: auto;
		border-radius: 6px;
		box-shadow: 0 4px 24px rgba(0,0,0,0.35);
		box-sizing: border-box;
	}

	.secretDialogTitle
	{
		font-size: 1.35em;
		font-weight: bold;
		margin-bottom: 8px;
	}

	.fieldLabel
	{
		display: block;
		font-weight: bold;
		font-size: 0.9em;
		color: #444444;
		margin: 10px 0 3px 0;
	}

	.fieldInput
	{
		display: block;
		width: 100%;
		box-sizing: border-box;
		padding: 7px 9px;
		border: 1px solid #AAAAAA;
		border-radius: 4px;
		font-size: 15px;
	}

	.valueInput
	{
		font-family: Consolas, monospace;
		resize: vertical;
		min-height: 90px;
	}

	.generateRow
	{
		margin-top: 6px;
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 6px 8px;
	}

		.generateRow .generateBtn
		{
			font-size: 0.9em;
			padding: 4px 10px;
		}

			.generateRow .generateBtn:disabled
			{
				opacity: 0.55;
				cursor: default;
			}

	.generateSizeLabel
	{
		font-size: 0.9em;
		color: #444444;
	}

	.generateSizeSelect
	{
		font-size: 0.9em;
		padding: 3px 4px;
		border: 1px solid #AAAAAA;
		border-radius: 3px;
		max-width: 100%;
	}

	.generateHint
	{
		margin: 4px 0 0 0;
		font-size: 0.85em;
	}

	.metadataSection
	{
		margin-top: 14px;
		border-top: 1px solid #E0E0E0;
		padding-top: 10px;
		font-size: 0.9em;
		color: #444444;
	}

	.metaRow
	{
		margin: 3px 0;
	}

	.metaLabel
	{
		display: inline-block;
		min-width: 72px;
		font-weight: bold;
		color: #666666;
	}

	.agentSyncList
	{
		margin-top: 8px;
	}

	.agentSyncItem
	{
		display: flex;
		align-items: center;
		gap: 6px;
		margin: 3px 0;
	}

	.syncedText
	{
		color: #0A5A0A;
	}

	.notSyncedText
	{
		color: #8B0000;
	}

	.secretDialogButtons
	{
		margin-top: 18px;
		display: flex;
		align-items: center;
	}

	.deleteBtn
	{
		margin-left: auto;
		margin-right: 0;
	}

	/* Narrow viewports */
	@media (max-width: 760px)
	{
		.listHeader
		{
			display: none;
		}

		.listHeader,
		.secretRow
		{
			grid-template-columns: minmax(0, 1fr) auto;
		}

		/* The "Last edit" column moves down below the name/value/sync information. */
		.lastEditCell
		{
			grid-column: 1 / -1;
			font-size: 0.85em;
		}
	}

	@media (max-width: 380px)
	{
		.card
		{
			padding: 8px;
		}

		.pageHeading
		{
			font-size: 26px;
		}
	}
</style>
